import {
	type ChangeEvent,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { FiChevronDown } from "react-icons/fi";

import { useQuery } from "~/hooks/useQuery";
import { applyDiscount } from "~/modules/sales/services";
import { insertToast } from "~/modules/toasts/services";
import { debouncer } from "~/utils/debounce";
import { subscriptionsCheckoutContext } from "../..";
import { discountFormContext } from ".";

const debounce = debouncer();
let controller = new AbortController();

export const DiscountInput: React.FC = () => {
	const query = useQuery();
	const { accumulateFormData, plan } = useContext(subscriptionsCheckoutContext);
	const { handleSetCoupon } = useContext(discountFormContext);

	const [open, setOpen] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>();
	const inputRef = useRef<HTMLInputElement>(null);

	const handleToggleDiscountInput = useCallback(() => {
		if (inputRef.current?.value) return;
		setOpen((s) => !s);
	}, []);

	const handleCouponInsert = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const code = e.target.value.trim().toUpperCase();
			inputRef.current!.value = code;

			controller.abort();
			controller = new AbortController();

			debounce(async () => {
				if (!code || code.length < 3) {
					handleSetCoupon(undefined);
					accumulateFormData({ coupon: undefined });
					return;
				}
				setLoading(() => true);
				applyDiscount({ code, planId: plan?.id ?? "" }, controller)
					.then((data) => {
						if (data) {
							handleSetCoupon(data);
							accumulateFormData({ coupon: code });
						} else {
							handleSetCoupon();
							accumulateFormData({ coupon: undefined });
						}
						setError("");
					})
					.catch((e: Error) => {
						handleSetCoupon();
						accumulateFormData({ coupon: undefined });
						insertToast({
							duration: 4,
							message: e.message,
							title: "Ops",
							type: "error",
						});
						setError(e.message);
					})
					.finally(() => {
						setLoading(() => false);
					});
			}, 1000);
		},
		[handleSetCoupon, accumulateFormData, plan?.id],
	);

	useEffect(() => {
		const abortController = new AbortController();
		const code = query.get("coupon");

		if (code && plan?.id) {
			setLoading(() => true);
			applyDiscount({ code, planId: plan?.id ?? "" }, controller)
				.then((data) => {
					if (data) {
						handleSetCoupon(data);
						accumulateFormData({ coupon: code });
						setOpen(true);
						inputRef.current!.value = data.code;
					} else {
						handleSetCoupon();
						accumulateFormData({ coupon: undefined });
					}
					setError("");
				})
				.catch((e: Error) => {
					handleSetCoupon();
					accumulateFormData({ coupon: undefined });
					insertToast({
						duration: 4,
						message: e.message,
						title: "Ops",
						type: "error",
					});
					setError(e.message);
				})
				.finally(() => {
					setLoading(() => false);
				});
		}

		return () => {
			abortController.abort();
		};
	}, [query, accumulateFormData, handleSetCoupon, plan?.id]);

	return (
		<div className="flex flex-col duration-200">
			<button
				className="flex flex-1 justify-between py-4"
				onClick={handleToggleDiscountInput}
			>
				<span>Aplicar cupom de desconto</span>
				<FiChevronDown
					size={24}
					data-shown={open}
					className="data-[shown=true]:rotate-180 duration-150"
				/>
			</button>
			<label
				htmlFor="discount_input"
				data-shown={open}
				className="data-[shown=true]:max-h-24 max-h-0 duration-150
          overflow-hidden"
			>
				<div
					data-error={!!error}
					className="flex flex-1 px-2 my-2 gap-2 rounded-md bg-white h-10
            focus-within:border-2 border-2 focus-within:border-gpa-blue-500
            data-[error=true]:border-gpa-red"
				>
					<input
						ref={inputRef}
						id="discount_input"
						className="outline-none flex-1 bg-transparent"
						type="text"
						onChange={handleCouponInsert}
					/>
					<div
						data-loading={loading}
						className="p-2 hidden data-[loading=true]:block"
					>
						<div
							className="h-5 w-5 border-2 rounded-full border-gpa-blue-500
              border-b-gpa-blue-50 animate-spin"
						></div>
					</div>
				</div>
				<p
					data-error={!!error}
					className="data-[error=true]:text-gpa-red data-[error=false]:hidden"
				>
					{error}
				</p>
			</label>
		</div>
	);
};
